// Layout shell — orchestrator only.
//
// Per the "Thinking in React" rule (see .claude/rules/thinking-in-react.md):
//
// 1. Hierarchy:
//      Layout
//        ├── useLayoutPermissions()  — collapses 12 usePermission calls
//        ├── useNavigation(perms)    — portal-aware nav config
//        ├── useSidebarBadges()      — supplier red-dot counts
//        ├── useNotifications()      — inbox + mark-read
//        ├── useModuleLinks()        — modules dropdown
//        ├── <AppShell.Header> AdminHeader
//        ├── <AppShell.Navbar> Sidebar
//        ├── <AppShell.Main>   Outlet
//        └── <Spotlight>
//
// 2. Static: every child is fed via props/return values; Layout does not
//    pass state down past `opened` + `onPick`.
// 3. Minimal state: `opened` (mobile navbar toggle) is the only piece
//    of state owned here. Everything else is derived from Redux/SWR.
// 4. State location: `opened` lives in Layout because Sidebar +
//    AppShell both need it. User/cart/auth are already in Redux.
// 5. Inverse data flow: Sidebar pushes `onPick` (link) back up so
//    Layout can close the mobile drawer; AdminHeader pushes
//    onLogout/onProfile/onNotificationClick to Layout for navigation
//    + tenant-aware redirects.
//
// PageLayout / PageSection / FormLayout are page-level helpers consumed
// by individual route components — kept here so existing imports
// (`from "../../components/layout"`) keep working.

import {
  AdminHeader,
  AppShell,
  Box,
  Flex,
  getModuleUrl,
  HelpButton,
  isLightBg,
  LanguageSwitcher,
  LoadingOverlay,
  Sidebar,
  useHelp,
  Text,
  Title,
  useTenant,
  useTheme,
  useTokenVars,
  type SidebarItem,
  type ProfileMenuSection,
} from "@dehub/theme"
import { Button, Group, Indicator, Stack } from "@mantine/core"
import { Spotlight, type SpotlightActionData } from "@mantine/spotlight"
import {
  BankIcon,
  KeyIcon,
  LockKeyIcon,
  MagnifyingGlassIcon,
  QuestionIcon,
  ShoppingCartSimpleIcon,
  UserCircleIcon,
} from "@phosphor-icons/react"
import React, { JSX, ReactNode, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { LogoutApi } from "../../apis"
import { persistor } from "../../store"
import { IAuth } from "../../store/Auth"
import { ICart } from "../../store/Cart"
import { currencyFormat } from "../../utils/currency"
import { message } from "../../utils/message"
import { renderName } from "../../utils/render-name"
import { AppsMenuWithBadge } from "./AppsMenuWithBadge"
import { Breadcrumb } from "./breadcrumb"
import { useLayoutPermissions } from "./hooks/use-layout-permissions"
import useSWR from "swr"
import { useModuleLinks } from "./hooks/use-module-links"
import { getModuleBadges } from "../../apis/network-badge"
import { useNavigation } from "./hooks/use-navigation"
import { useNotifications } from "./hooks/use-notifications"
import { useSidebarBadges } from "./hooks/use-sidebar-badges"
import classes from "./layout.module.css"
import { PageShell } from "../ui/page-shell"

// One-shot flag so the default-page perm error doesn't spam the user.
let __DEFAULT_PAGE_GUARD_FIRED__ = false

const ACTIVE_ALIASES: ReadonlyArray<readonly [string, string]> = [
  ["/buyer-sales/product/", "/buyer-sales/new"],
  ["/buyer-sales/checkout", "/buyer-sales/new"],
  ["/buyer-sales/success", "/buyer-sales/new"],
  ["/buyer-app/orders/", "/buyer-app/my-returns"],
  ["/pending-deliveries", "/supplier-delivery"],
  ["/active-deliveries", "/supplier-delivery"],
  ["/failed-deliveries", "/supplier-delivery"],
  ["/delivery-routes", "/supplier-delivery"],
  ["/handover", "/supplier-delivery"],
  ["/picking", "/supplier-shipping"],
  ["/damaged-inventory", "/returns"],
]

export function Layout() {
  useTokenVars()
  const { tokens } = useTheme()
  const tenant = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation("layout")

  // ── State (Step 3) ────────────────────────────────────────────────────
  const [opened, setOpened] = useState<boolean>(false)

  // ── Derived from Redux / hooks (not state) ───────────────────────────
  const { user, accessToken } = useSelector((s: { auth: IAuth }) => s.auth)
  const cartItems = useSelector((s: { cart: ICart }) => s.cart.items)

  const portal = user?.currentBusiness?.type === "SUPPLIER" ? "SUPPLIER" : "BUYER"
  const isBuyer = portal === "BUYER"
  const isSupplier = portal === "SUPPLIER"

  const perms = useLayoutPermissions()
  const navConfig = useNavigation(perms, portal)

  // Default-page guard. If the current path is the root and the user
  // can't see the dashboard, surface the perm error once per session.
  useEffect(() => {
    const isRoot = !location.pathname.split("/")[1]
    if (isRoot && !perms.dashboardGuard.hasAccess && !__DEFAULT_PAGE_GUARD_FIRED__) {
      message.error(perms.dashboardGuard.text)
      __DEFAULT_PAGE_GUARD_FIRED__ = true
    }
  }, [location, perms.dashboardGuard])

  // ── Sidebar items + active resolution ────────────────────────────────
  const badges = useSidebarBadges({ accessToken, isSupplier, isBuyer })

  const items: SidebarItem[] = useMemo(
    () =>
      navConfig.map((n) => ({
        key: n.link,
        label: t(n.titleKey),
        link: n.link,
        icon: n.icon,
        disabled: !n.view,
        // O20: an item the user cannot open says why on hover (theme 2.8.5 SidebarItem.disabledReason).
        disabledReason: n.view ? undefined : t("common:permission.accessDenied"),
        badgeCount: badges.byLink[n.link],
      })),
    [navConfig, badges.byLink, t],
  )

  const activeKey = useMemo(() => {
    // Pages that sit under another item's path prefix but belong to a different menu item
    // (product, checkout, success are steps of «Захиалга», not of «Худалдан авалт»).
    const alias = ACTIVE_ALIASES.find(([prefix, link]) =>
      location.pathname.startsWith(prefix) && items.some((it) => it.link === link))
    if (alias) return alias[1]
    const match = items.find(
      (it) =>
        location.pathname === it.link ||
        (it.link !== "/" && location.pathname.startsWith(it.link + "/")),
    )
    return match?.key
  }, [items, location.pathname])

  // ── Notifications + modules ──────────────────────────────────────────
  const { notifications, onMarkAllNotifsRead, onNotificationClick } =
    useNotifications({ accessToken, hasUser: !!user, isBuyer })

  const ownedModules = useMemo(
    () =>
      Array.from(
        new Set(
          (user?.permissions || [])
            .map((p) => p.module)
            .filter((mod): mod is string => !!mod),
        ),
      ),
    [user?.permissions],
  )

  const { data: moduleBadges } = useSWR(
    accessToken ? ["module-badges", isBuyer] : null,
    () => getModuleBadges(isBuyer),
    { revalidateOnFocus: false, dedupingInterval: 60000, shouldRetryOnError: false },
  )

  const modules = useModuleLinks({ t, accessToken, ownedModules, isBuyer, badges: moduleBadges })

  // ── Header callbacks (Step 5 inverse data flow) ──────────────────────
  const onLogout = async () => {
    try {
      await LogoutApi.logout()
    } catch (e) {
      const err = e as { message?: string }
      message.error(err?.message ?? "")
    } finally {
      // Persisted token-ийг reload-аас өмнө устгаж, хуучин token rehydrate
      // хийгдэхээс сэргийлнэ.
      await persistor.purge()
      window.location.href = `${getModuleUrl("", tenant?.modulesDomain)}/login?logout=1`
    }
  }

  const onProfile = () => {
    window.location.href = `${getModuleUrl("", tenant?.modulesDomain)}/auth/profile/dan`
  }

  const onOpenCart = () => {
    if (location.pathname.startsWith("/buyer-sales/new")) {
      window.dispatchEvent(new Event("toggle-cart-drawer"))
    } else {
      navigate("/buyer-sales/new", { state: { openCart: true } })
    }
  }

  // ── Spotlight nav search ─────────────────────────────────────────────
  const navActions: SpotlightActionData[] = useMemo(
    () =>
      navConfig
        .filter((n) => n.view)
        .map((n) => ({
          id: n.link,
          label: t(n.titleKey),
          onClick: () => navigate(n.link),
          leftSection: <n.icon size={18} weight="duotone" />,
        })),
    [navConfig, t, navigate],
  )

  // ── Header user display ──────────────────────────────────────────────
  const userName = renderName(user) || ""
  const userEmail = user?.email || ""
  const userInitial = (userName.trim().charAt(0) || "U").toUpperCase()
  const currentBusiness = (user as { currentBusiness?: { profileName?: string; partnerName?: string } })?.currentBusiness
  const businessName = currentBusiness?.profileName || currentBusiness?.partnerName || ""
  const pageTitle = t("header.moduleTitle", { defaultValue: "ЗАХИАЛГА" })
  const cpUrl = useMemo(
    () => getModuleUrl("cp", tenant?.modulesDomain),
    [tenant?.modulesDomain],
  )
  // ── In-app help (@dehub/help): header «?», profile switch, ⌘K action ──
  const { hintsOn, setHintsOn, openDrawer: openHelpDrawer } = useHelp()
  const profileSections: ProfileMenuSection[] = useMemo(() => {
    const authRoot = getModuleUrl("", tenant?.modulesDomain)
    return [
      {
        items: [
          {
            icon: QuestionIcon,
            label: t("help:profileSwitch"),
            description: t("help:profileSwitchDesc"),
            kind: "switch" as const,
            checked: hintsOn,
            onToggle: (on: boolean) => setHintsOn(on, "profile"),
          },
        ],
      },
      {
        items: [
          { icon: UserCircleIcon, label: t("header.profileMenu.myInfo"), href: `${authRoot}/auth/profile/dan` },
          { icon: BankIcon, label: t("header.profileMenu.partnerManagement"), href: `${getModuleUrl("partner", tenant?.modulesDomain)}/access?token=${accessToken ?? ""}` },
        ],
      },
      {
        items: [
          { icon: KeyIcon, label: t("header.profileMenu.changePin"), href: `${authRoot}/auth/profile/dan?profileAction=pin` },
          { icon: LockKeyIcon, label: t("header.profileMenu.changePassword"), href: `${authRoot}/auth/profile/dan?profileAction=password` },
        ],
      },
    ]
  }, [tenant?.modulesDomain, accessToken, t, hintsOn, setHintsOn])

  return (
    <AppShell
      padding={0}
      header={{ height: 60 }}
      navbar={{
        // Ord_Shell (O33): the rail is 104px in this module, not the tenant token's 120.
        width: 104,
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      styles={{
        header: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 200 },
        // Std: the work area is white; pages sit directly on it (no grey canvas + white card).
        main: { backgroundColor: "var(--mantine-color-white)" },
      }}
    >
      <AppShell.Header>
        <AdminHeader
          tenant={{
            name: businessName,
            logo: tenant?.logo || null,
            domain: (tenant as { domain?: string })?.domain || "",
          }}
          pageTitle={pageTitle}
          headerGradient={tokens.header.gradient}
          headerTextColor={tokens.header.textColor}
          userName={userName}
          userEmail={userEmail}
          userInitial={userInitial}
          userAvatar={(user as any)?.avatar || undefined}
          onProfile={onProfile}
          onLogout={onLogout}
          onSwitchTenant={() => {
            window.location.href = `${getModuleUrl("", tenant?.modulesDomain)}/businesses`
          }}
          notifications={notifications}
          onMarkAllNotifsRead={onMarkAllNotifsRead}
          onNotificationClick={onNotificationClick}
          notificationsViewAllHref={isBuyer ? "/buyer-app/notifications" : undefined}
          preNotificationsSlot={<AppsMenuWithBadge modules={modules} align="right" />}
          helpSlot={<HelpButton light={isLightBg(tokens.header.gradient)} />}
          localeSlot={
            <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <HeaderLocaleSlot
                isBuyer={isBuyer}
                cartCount={cartItems.length}
                cartAmount={cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)}
                onOpenCart={onOpenCart}
              />
            </Box>
          }
          logoHref={cpUrl}
          profileSections={profileSections}
        />
      </AppShell.Header>

      <AppShell.Navbar p={0} style={{ background: "transparent", border: "none" }}>
        <Sidebar
          items={items}
          activeKey={activeKey}
          onPick={(_key, link) => {
            navigate(link)
            setOpened(false)
          }}
          headerStyle="dark"
        />
      </AppShell.Navbar>

      <AppShell.Main>
        <PageShell>
          <Outlet />
        </PageShell>
      </AppShell.Main>

      <Spotlight
        actions={[
          ...navActions,
          { id: "help", label: t("help:openHelp"), onClick: () => openHelpDrawer({ trigger: "link" }), leftSection: <QuestionIcon size={18} weight="duotone" /> },
        ]}
        nothingFound={t("header.searchNotFound", { defaultValue: "Илэрц олдсонгүй" })}
        highlightQuery
        shortcut={["mod + K", "mod + P", "/"]}
        searchProps={{
          leftSection: <MagnifyingGlassIcon size={18} weight="duotone" />,
          placeholder: t("header.searchPlaceholder", { defaultValue: "Цэс, хуудас хайх..." }),
        }}
        radius="md"
      />
    </AppShell>
  )
}

// ───── Header locale slot — pure component, props in / events out ─────

interface HeaderLocaleSlotProps {
  isBuyer: boolean
  cartCount: number
  cartAmount: number
  onOpenCart: () => void
}

function HeaderLocaleSlot({ isBuyer, cartCount, cartAmount, onOpenCart }: HeaderLocaleSlotProps) {
  const { t } = useTranslation("order")
  // B28: the cart button names itself in the UI language and shows the cart amount (canvas OrdB_Cart header).
  const cartLabel = t("cart.headerLabel", { count: cartCount, amount: currencyFormat(cartAmount) })
  return (
    <Group gap={4} align="center" wrap="nowrap">
      {isBuyer && (
        <Indicator
          label={cartCount}
          disabled={cartCount === 0}
          color="red"
          size={16}
          offset={6}
          withBorder
          styles={{ indicator: { fontWeight: 700, fontSize: 10 } }}
        >
          <Button
            variant="subtle"
            color="gray"
            h={36}
            px="sm"
            radius="md"
            onClick={onOpenCart}
            aria-label={cartLabel}
            title={cartLabel}
            data-cart-fly-target
            leftSection={<ShoppingCartSimpleIcon size={20} weight="regular" />}
            styles={{
              root: { color: "inherit" },
              label: { fontFamily: "var(--mantine-font-family-monospace)", fontSize: "var(--nw-fz-body)", fontWeight: 600 },
            }}
          >
            {currencyFormat(cartAmount)}
          </Button>
        </Indicator>
      )}
      <LanguageSwitcher />
    </Group>
  )
}

// ───── PageLayout / PageSection / FormLayout — page-level helpers ─────
//
// These are not part of the Layout shell — they're presentational
// wrappers individual pages opt into. Kept in this file so existing
// imports stay stable.

type PageProps = Readonly<{
  title: string | JSX.Element
  subTitle?: string | JSX.Element
  icon?: ReactNode
  extra?: React.ReactNode[]
  children: React.ReactNode
  loading?: boolean
  breadcrumb?: { to?: string; label: string }[]
  /** Override the page background. Accepts a CSS color or Mantine var token. */
  bgColor?: string
  /** When true, drop the white card frame (padding, background, border-radius). */
  noFrame?: boolean
}>

export function PageLayout({
  title,
  subTitle,
  breadcrumb,
  extra,
  children,
  loading = false,
  bgColor,
  noFrame = false,
}: PageProps) {
  const frameStyle = noFrame
    ? { padding: 0, background: "transparent", boxShadow: "none", borderRadius: 0 }
    : undefined
  const mergedStyle = {
    ...(frameStyle ?? {}),
    ...(bgColor ? { background: bgColor } : {}),
  }
  return (
    <Box
      className={classes.pageLayout}
      style={Object.keys(mergedStyle).length ? mergedStyle : undefined}
    >
      <LoadingOverlay visible={loading} />
      {/* Breadcrumb sits tight above the title; the page gap only separates the header from the content. */}
      <Stack gap={4}>
        {breadcrumb && <Breadcrumb data={breadcrumb} />}
        <Flex align="center" justify="space-between">
          <Flex align="center" gap="md">
            {/* Canvas pages carry no icon tile beside the title; `icon` is kept for API compatibility. */}
            <Box>
              <Title order={2} className={classes.title}>
                {title}
              </Title>
              {subTitle && <Text className={classes.subTitle}>{subTitle}</Text>}
            </Box>
          </Flex>
          <Flex gap="sm" align="center">
            {extra}
          </Flex>
        </Flex>
      </Stack>
      {children}
    </Box>
  )
}

/** Modal footer per Ord_SalesModals: hairline across the modal, actions on the right. */
export function ModalFooter({ children }: Readonly<{ children: React.ReactNode }>) {
  return <Group className={classes.modalFooter} justify="flex-end" gap="sm">{children}</Group>
}

type SectionProps = Readonly<{
  left?: JSX.Element[]
  right?: JSX.Element[]
}>

export function PageSection({ left, right }: SectionProps) {
  return (
    <Box className={classes.pageSection}>
      <Box className={classes.sectionElement}>{left}</Box>
      <Box className={classes.sectionElement}>{right}</Box>
    </Box>
  )
}

type FormProps = Readonly<{
  title: string | JSX.Element
  subTitle?: string | JSX.Element
  extra?: React.ReactNode[]
  children: React.ReactNode
  my?: number | string
}>

export function FormLayout({ title, subTitle, extra = [], children, my = 0 }: FormProps) {
  return (
    <Box className={classes.formLayout} my={my}>
      <Flex align="center" justify="space-between">
        <Box>
          <Title order={2} className={classes.title}>
            {title}
          </Title>
          <Text className={classes.subTitle}>{subTitle}</Text>
        </Box>
        <Flex gap="sm" align="center">
          {extra}
        </Flex>
      </Flex>
      {children}
    </Box>
  )
}
